import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import PharmeasyLayout from "@/components/PharmeasyLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Calculator, Calendar, RefreshCw, Loader2, TrendingUp, 
  Package, ArrowDownRight, CheckCircle, ChevronLeft, ChevronRight,
  Download, Settings, Save, Receipt, Building, CreditCard, 
  CalendarDays, IndianRupee, Database, FileText, X, Landmark, Wallet, Users
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { format } from "date-fns";
import VendorExpenses from "./VendorExpenses";

const PharmeasyBilling = ({ user, onLogout }) => {
  // Use the configured API from App.js which handles HTTPS properly
  const BILLING_API = API;
  
  // Helper function to check if a billing period is complete (can generate invoice)
  const isPeriodComplete = (endDate) => {
    if (!endDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset to start of day
    const periodEnd = new Date(endDate);
    periodEnd.setHours(23, 59, 59, 999); // End of the period day
    return today > periodEnd; // Invoice can be generated after the period ends
  };
  
  // Main section tab
  const [mainTab, setMainTab] = useState("data");
  const [sectionTab, setSectionTab] = useState("client");
  
  // Financial summary
  const [financialSummary, setFinancialSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  
  // Billing Data state
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [generatingInvoice, setGeneratingInvoice] = useState(null);  // Track which invoice is being generated
  const [billingData, setBillingData] = useState([]);
  const [weeklyData, setWeeklyData] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [missingDates, setMissingDates] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [weekPage, setWeekPage] = useState(1);
  const [weekTotalPages, setWeekTotalPages] = useState(1);
  const [monthPage, setMonthPage] = useState(1);
  const [monthTotalPages, setMonthTotalPages] = useState(1);
  const [riderDailyData, setRiderDailyData] = useState([]);
  const [riderDailyPage, setRiderDailyPage] = useState(1);
  const [riderDailyTotalPages, setRiderDailyTotalPages] = useState(1);
  const [totalInvoiceCount, setTotalInvoiceCount] = useState(0);
  const [totalBilledShipments, setTotalBilledShipments] = useState(0);
  const [calculating, setCalculating] = useState(false);
  const [activeDataTab, setActiveDataTab] = useState("date");
  const [weekDefinitions, setWeekDefinitions] = useState(null);
  
  // Invoice Modal state
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(new Date());
  const [pendingInvoiceParams, setPendingInvoiceParams] = useState(null);
  const [billedForward, setBilledForward] = useState(0);
  const [billedReturn, setBilledReturn] = useState(0);
  
  // Billing Settings state
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingSignatory, setUploadingSignatory] = useState(false);
  
  // Payment recording dialog
  const [paymentDialog, setPaymentDialog] = useState(null);
  const [paymentForm, setPaymentForm] = useState({ amount: "", payment_date: new Date().toISOString().slice(0, 10), payment_mode: "", reference_number: "", notes: "" });
  const [recordingPayment, setRecordingPayment] = useState(false);
  
  const [settings, setSettings] = useState({
    billing_frequency: "daily",
    billing_cron_enabled: false,
    billing_cron_time: "00:30",
    forward_charge: {
      charge_type: "per_order",
      charge_per_order: 0,
      minimum_billing_amount: 0,
      fixed_rate_daily: 0,
      fixed_rate_weekly: 0,
      fixed_rate_monthly: 0,
      gst_percentage: 18,
    },
    return_charge: {
      charge_type: "per_order",
      charge_per_order: 0,
      minimum_billing_amount: 0,
      fixed_rate_daily: 0,
      fixed_rate_weekly: 0,
      fixed_rate_monthly: 0,
      gst_percentage: 18,
    },
    bill_to: { name: "", address: "", gst_number: "", state_code: "" },
    bill_from: { name: "", address: "", gst_number: "", state_code: "" },
    bank_details: { account_number: "", bank_name: "", ifsc_code: "", city: "", cheque_payable_to: "" },
    week_definitions: {
      week_1: { start: 1, end: 7 },
      week_2: { start: 8, end: 14 },
      week_3: { start: 15, end: 21 },
      week_4: { start: 22, end: 31 },
    },
    signatory_image_url: null,
    last_updated_at: null,
    last_updated_by: null,
  });

  useEffect(() => {
    // Always fetch settings to get billing_frequency for invoice button visibility
    fetchSettings();
    fetchFinancialSummary();
  }, []);
  
  useEffect(() => {
    if (mainTab === "data") {
      fetchData();
    } else if (mainTab === "settings") {
      fetchSettings();
    }
  }, [mainTab]);
  
  useEffect(() => {
    if (mainTab === "data") {
      fetchData();
    }
  }, [page, activeDataTab, weekPage, monthPage, riderDailyPage]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const requests = [
        axios.get(`${BILLING_API}/pharmeasy-billing/summary`),
        axios.get(`${BILLING_API}/pharmeasy-billing/missing-dates`),
      ];
      
      // Also fetch invoice count
      const invoiceCountReq = axios.get(`${BILLING_API}/pharmeasy-billing/invoice-count`).catch(() => ({ data: { count: 0 } }));
      
      if (activeDataTab === "date") {
        requests.push(axios.get(`${BILLING_API}/pharmeasy-billing/?page=${page}&size=30`));
      } else if (activeDataTab === "week") {
        requests.push(axios.get(`${BILLING_API}/pharmeasy-billing/weekly?page=${weekPage}&size=10`));
      } else if (activeDataTab === "month") {
        requests.push(axios.get(`${BILLING_API}/pharmeasy-billing/monthly?page=${monthPage}&size=12`));
      } else if (activeDataTab === "rider-daily") {
        requests.push(axios.get(`${BILLING_API}/pharmeasy-billing/rider-daily?page=${riderDailyPage}&size=30`));
      }
      
      const results = await Promise.all(requests);
      const invoiceCountRes = await invoiceCountReq;
      
      setSummary(results[0].data);
      setMissingDates(results[1].data.missing_dates || []);
      setTotalInvoiceCount(invoiceCountRes.data.count || 0);
      setTotalBilledShipments(invoiceCountRes.data.total_billed_shipments || 0);
      
      if (activeDataTab === "date") {
        setBillingData(results[2].data.data || []);
        setTotalPages(results[2].data.total_pages || 1);
      } else if (activeDataTab === "week") {
        setWeeklyData(results[2].data.data || []);
        setWeekTotalPages(results[2].data.total_pages || 1);
        setWeekDefinitions(results[2].data.week_definitions || null);
      } else if (activeDataTab === "month") {
        setMonthlyData(results[2].data.data || []);
        setMonthTotalPages(results[2].data.total_pages || 1);
      } else if (activeDataTab === "rider-daily") {
        setRiderDailyData(results[2].data.data || []);
        setRiderDailyTotalPages(results[2].data.total_pages || 1);
      }
    } catch (err) {
      toast.error("Failed to load billing data");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    setSettingsLoading(true);
    try {
      const res = await axios.get(`${BILLING_API}/pharmeasy-billing-settings/`);
      setSettings(res.data);
    } catch (err) {
      toast.error("Failed to load billing settings");
      console.error(err);
    } finally {
      setSettingsLoading(false);
    }
  };

  const fetchFinancialSummary = async () => {
    setSummaryLoading(true);
    try {
      const res = await axios.get(`${BILLING_API}/pharmeasy-expenses/financial-summary`);
      setFinancialSummary(res.data);
    } catch (err) {
      console.error("Failed to load financial summary", err);
    } finally {
      setSummaryLoading(false);
    }
  };

  const openPaymentDialog = (weekItem) => {
    setPaymentDialog(weekItem);
    const due = (weekItem.invoice_amount || 0) - (weekItem.amount_received || 0);
    setPaymentForm({ amount: due > 0 ? String(due) : "", payment_date: new Date().toISOString().slice(0, 10), payment_mode: "", reference_number: "", notes: "" });
  };

  const handleRecordPayment = async () => {
    if (!paymentForm.amount || !paymentDialog?.invoice_id) return;
    setRecordingPayment(true);
    try {
      await axios.post(`${BILLING_API}/pharmeasy-expenses/client-payments`, {
        invoice_id: paymentDialog.invoice_id,
        amount: parseFloat(paymentForm.amount),
        payment_date: paymentForm.payment_date,
        payment_mode: paymentForm.payment_mode,
        reference_number: paymentForm.reference_number,
        notes: paymentForm.notes,
      });
      toast.success("Payment recorded");
      setPaymentDialog(null);
      fetchData();
      fetchFinancialSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to record payment");
    } finally {
      setRecordingPayment(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${BILLING_API}/pharmeasy-billing-settings/`, settings);
      setSettings(res.data);
      toast.success("Billing settings saved successfully");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save billing settings");
    } finally {
      setSaving(false);
    }
  };

  const calculateForDate = async (dateStr) => {
    setCalculating(true);
    try {
      await axios.post(`${BILLING_API}/pharmeasy-billing/calculate/${dateStr}`);
      toast.success(`Billing calculated for ${dateStr}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to calculate billing");
    } finally {
      setCalculating(false);
    }
  };

  const calculateAllMissing = async () => {
    if (!window.confirm(`Calculate billing for ${missingDates.length} missing dates?`)) return;
    setCalculating(true);
    try {
      await axios.post(`${BILLING_API}/pharmeasy-billing/calculate-missing`);
      toast.success("Billing calculated for all missing dates");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to calculate billing");
    } finally {
      setCalculating(false);
    }
  };

  const downloadCSV = async (type) => {
    setDownloading(true);
    try {
      let endpoint = `${BILLING_API}/pharmeasy-billing/download/datewise`;
      let filename = "billing_date_wise.csv";
      
      if (type === "week") {
        endpoint = `${BILLING_API}/pharmeasy-billing/download/weekly`;
        filename = "billing_week_wise.csv";
      } else if (type === "month") {
        endpoint = `${BILLING_API}/pharmeasy-billing/download/monthly`;
        filename = "billing_month_wise.csv";
      } else if (type === "rider-daily") {
        endpoint = `${BILLING_API}/pharmeasy-billing/download/rider-daily`;
        filename = "rider_daily_delivery.csv";
      }
      
      const response = await axios.get(endpoint, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Downloaded ${filename}`);
    } catch (err) {
      toast.error("Failed to download CSV");
      console.error(err);
    } finally {
      setDownloading(false);
    }
  };

  // Open invoice modal - store params for later use
  const openInvoiceModal = (type, params) => {
    setPendingInvoiceParams({ type, params });
    setInvoiceNumber("");
    setInvoiceDate(new Date());
    setBilledForward(params.forward_delivered || 0);
    setBilledReturn(params.return_dispatched || 0);
    setInvoiceModalOpen(true);
  };

  // Close invoice modal
  const closeInvoiceModal = () => {
    setInvoiceModalOpen(false);
    setPendingInvoiceParams(null);
    setInvoiceNumber("");
    setInvoiceDate(new Date());
    setBilledForward(0);
    setBilledReturn(0);
  };

  // Download existing invoice (without modal, uses stored invoice number)
  const downloadExistingInvoice = async (type, params) => {
    const invoiceKey = `existing-${type}-${JSON.stringify(params)}`;
    setGeneratingInvoice(invoiceKey);
    
    try {
      let endpoint = "";
      let filename = "";
      const invoiceNum = params.existingInvoice || "invoice";
      
      if (type === "daily") {
        endpoint = `${BILLING_API}/pharmeasy-billing/get-existing-invoice/daily/${params.date}`;
        filename = `invoice_${invoiceNum.replace(/\//g, '_')}_${params.date}.pdf`;
      } else if (type === "weekly") {
        endpoint = `${BILLING_API}/pharmeasy-billing/get-existing-invoice/weekly?year=${params.year}&month=${params.month}&week=${params.week}`;
        filename = `invoice_${invoiceNum.replace(/\//g, '_')}_W${params.week}_${params.monthName}_${params.year}.pdf`;
      } else if (type === "monthly") {
        endpoint = `${BILLING_API}/pharmeasy-billing/get-existing-invoice/monthly?year=${params.year}&month=${params.month}`;
        filename = `invoice_${invoiceNum.replace(/\//g, '_')}_${params.monthName}_${params.year}.pdf`;
      }
      
      const response = await axios.get(endpoint, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Invoice downloaded: ${filename}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to download invoice");
      console.error(err);
    } finally {
      setGeneratingInvoice(null);
    }
  };

  // Actually generate the invoice after user enters number
  const generateInvoice = async () => {
    if (!pendingInvoiceParams || !invoiceNumber.trim()) {
      toast.error("Please enter an invoice number");
      return;
    }
    
    const { type, params } = pendingInvoiceParams;
    const invoiceKey = `${type}-${JSON.stringify(params)}`;
    setGeneratingInvoice(invoiceKey);
    closeInvoiceModal();
    
    try {
      let endpoint = "";
      let filename = "";
      
      if (type === "daily") {
        endpoint = `${BILLING_API}/pharmeasy-billing/generate-invoice/daily/${params.date}`;
        filename = `invoice_${invoiceNumber.replace(/\//g, '_')}_${params.date}.pdf`;
      } else if (type === "weekly") {
        endpoint = `${BILLING_API}/pharmeasy-billing/generate-invoice/weekly?year=${params.year}&month=${params.month}&week=${params.week}`;
        filename = `invoice_${invoiceNumber.replace(/\//g, '_')}_W${params.week}_${params.monthName}_${params.year}.pdf`;
      } else if (type === "monthly") {
        endpoint = `${BILLING_API}/pharmeasy-billing/generate-invoice/monthly?year=${params.year}&month=${params.month}`;
        filename = `invoice_${invoiceNumber.replace(/\//g, '_')}_${params.monthName}_${params.year}.pdf`;
      }
      
      const response = await axios.post(endpoint, { 
        invoice_number: invoiceNumber.trim(),
        billed_quantity_forward: billedForward,
        billed_quantity_return: billedReturn,
        invoice_date: format(invoiceDate, "dd/MM/yyyy")
      }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Invoice generated: ${filename}`);
      // Refresh data to show the new invoice number
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate invoice");
      console.error(err);
    } finally {
      setGeneratingInvoice(null);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "Never";
    try {
      return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
    } catch {
      return iso;
    }
  };

  // Settings update helpers
  const updateForwardCharge = (field, value) => {
    setSettings(prev => ({ ...prev, forward_charge: { ...prev.forward_charge, [field]: value } }));
  };
  const updateReturnCharge = (field, value) => {
    setSettings(prev => ({ ...prev, return_charge: { ...prev.return_charge, [field]: value } }));
  };
  const updateBillTo = (field, value) => {
    setSettings(prev => ({ ...prev, bill_to: { ...prev.bill_to, [field]: value } }));
  };
  const updateBillFrom = (field, value) => {
    setSettings(prev => ({ ...prev, bill_from: { ...prev.bill_from, [field]: value } }));
  };
  const updateBankDetails = (field, value) => {
    setSettings(prev => ({ ...prev, bank_details: { ...prev.bank_details, [field]: value } }));
  };
  const updateWeekDef = (weekKey, field, value) => {
    setSettings(prev => ({
      ...prev,
      week_definitions: {
        ...prev.week_definitions,
        [weekKey]: { ...prev.week_definitions[weekKey], [field]: parseInt(value) || 0 }
      }
    }));
  };

  // Handle signatory image upload
  const handleSignatoryUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
      toast.error("Only PNG and JPEG images are allowed");
      return;
    }
    
    // Validate file size (max 500KB)
    if (file.size > 500 * 1024) {
      toast.error("File size must be less than 500KB");
      return;
    }
    
    setUploadingSignatory(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${BILLING_API}/pharmeasy-billing-settings/signatory-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSettings(prev => ({ ...prev, signatory_image_url: response.data.image_url }));
      toast.success("Signatory image uploaded successfully");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to upload signatory image");
    } finally {
      setUploadingSignatory(false);
      event.target.value = ''; // Reset file input
    }
  };

  // Handle signatory image removal
  const handleRemoveSignatory = async () => {
    try {
      await axios.delete(`${BILLING_API}/pharmeasy-billing-settings/signatory-image`);
      setSettings(prev => ({ ...prev, signatory_image_url: null }));
      toast.success("Signatory image removed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove signatory image");
    }
  };

  // Universal Loader for data tab
  if (sectionTab === "client" && mainTab === "data" && loading) {
    return (
      <PharmeasyLayout user={user} onLogout={onLogout}>
        <div className="flex flex-col items-center justify-center h-[60vh]">
          <Loader2 className="w-12 h-12 animate-spin text-emerald-500" />
          <p className="text-slate-500 mt-4 text-lg">Loading Billing Data...</p>
        </div>
      </PharmeasyLayout>
    );
  }

  const fmtCurrency = (v) => v != null ? `${Number(v).toLocaleString("en-IN", { minimumFractionDigits: 0 })}` : "0";

  return (
    <PharmeasyLayout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="pharmeasy-billing">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-3">
              <Receipt className="w-8 h-8 text-slate-600" />
              Billing
            </h1>
            <p className="text-slate-500 mt-1">Manage client billing and vendor expenses</p>
          </div>
          
          <div className="flex gap-4">
            {sectionTab === "client" && mainTab === "data" && missingDates.length > 0 && (
              <Button 
                variant="outline"
                onClick={calculateAllMissing}
                disabled={calculating}
                className="text-amber-600 border-amber-300 hover:bg-amber-50"
              >
                {calculating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Calculator className="w-4 h-4 mr-2" />}
                Calculate Missing ({missingDates.length})
              </Button>
            )}
            {sectionTab === "client" && mainTab === "data" && (
              <Button variant="outline" onClick={fetchData} disabled={loading}>
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            )}
            {sectionTab === "client" && mainTab === "settings" && (
              <>
                <Button variant="outline" onClick={fetchSettings} disabled={settingsLoading}>
                  <RefreshCw className={`w-4 h-4 mr-2 ${settingsLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                <Button onClick={handleSaveSettings} disabled={saving} style={{ backgroundColor: "#1B8C3D" }} data-testid="save-billing-settings-btn">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  {saving ? "Saving..." : "Save Settings"}
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Financial Summary Cards */}
        {financialSummary && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <Card className="border-0 shadow-md bg-gradient-to-br from-blue-50 to-blue-100">
              <CardContent className="pt-4 pb-4">
                <p className="text-xs font-medium text-blue-600 uppercase tracking-wide">Total Invoice Raised</p>
                <p className="text-xl font-bold text-blue-800 mt-1 flex items-center">
                  <IndianRupee className="w-4 h-4 mr-0.5" />{fmtCurrency(financialSummary.total_invoice_raised)}
                </p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-md bg-gradient-to-br from-amber-50 to-amber-100">
              <CardContent className="pt-4 pb-4">
                <p className="text-xs font-medium text-amber-600 uppercase tracking-wide">Payment Dues from Client</p>
                <p className="text-xl font-bold text-amber-800 mt-1 flex items-center">
                  <IndianRupee className="w-4 h-4 mr-0.5" />{fmtCurrency(financialSummary.total_payment_dues)}
                </p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-md bg-gradient-to-br from-red-50 to-red-100">
              <CardContent className="pt-4 pb-4">
                <p className="text-xs font-medium text-red-600 uppercase tracking-wide">Total Expense</p>
                <p className="text-xl font-bold text-red-800 mt-1 flex items-center">
                  <IndianRupee className="w-4 h-4 mr-0.5" />{fmtCurrency(financialSummary.total_expense)}
                </p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-md bg-gradient-to-br from-violet-50 to-violet-100">
              <CardContent className="pt-4 pb-4">
                <p className="text-xs font-medium text-violet-600 uppercase tracking-wide">Avg Monthly Expense</p>
                <p className="text-xl font-bold text-violet-800 mt-1 flex items-center">
                  <IndianRupee className="w-4 h-4 mr-0.5" />{fmtCurrency(financialSummary.avg_monthly_expense)}
                </p>
                <p className="text-xs text-violet-500 mt-0.5">Excl. one-time</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-md bg-gradient-to-br from-emerald-50 to-emerald-100">
              <CardContent className="pt-4 pb-4">
                <p className="text-xs font-medium text-emerald-600 uppercase tracking-wide">Avg Monthly Invoice Payment</p>
                <p className="text-xl font-bold text-emerald-800 mt-1 flex items-center">
                  <IndianRupee className="w-4 h-4 mr-0.5" />{fmtCurrency(financialSummary.avg_monthly_invoice_payment)}
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Top-level Client / Vendor Tabs */}
        <Tabs value={sectionTab} onValueChange={setSectionTab}>
          <TabsList className="mb-6 bg-slate-100">
            <TabsTrigger value="client" className="gap-2" data-testid="billing-client-tab">
              <Building className="w-4 h-4" /> Client
            </TabsTrigger>
            <TabsTrigger value="vendor" className="gap-2" data-testid="billing-vendor-tab">
              <Users className="w-4 h-4" /> Vendor
            </TabsTrigger>
          </TabsList>

          {/* ============ CLIENT TAB ============ */}
          <TabsContent value="client">

        {/* Main Tabs: Billing Data | Billing Settings */}
        <Tabs value={mainTab} onValueChange={setMainTab}>
          <TabsList className="mb-6 bg-slate-100">
            <TabsTrigger value="data" className="gap-2" data-testid="billing-data-tab">
              <Database className="w-4 h-4" /> Bill Data
            </TabsTrigger>
            <TabsTrigger value="settings" className="gap-2" data-testid="billing-settings-tab">
              <Settings className="w-4 h-4" /> Billing Settings
            </TabsTrigger>
          </TabsList>

          {/* ============ BILLING DATA TAB ============ */}
          <TabsContent value="data">
            {/* Summary Cards */}
            {summary && (
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
                <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-50 to-blue-100">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-blue-600">Forward Delivered</p>
                        <p className="text-3xl font-bold text-blue-800">{summary.total_forward_delivered?.toLocaleString()}</p>
                      </div>
                      <div className="w-12 h-12 bg-blue-200 rounded-full flex items-center justify-center">
                        <Package className="w-6 h-6 text-blue-700" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-lg bg-gradient-to-br from-teal-50 to-teal-100">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-teal-600">Return Dispatched</p>
                        <p className="text-3xl font-bold text-teal-800">{summary.total_return_dispatched?.toLocaleString()}</p>
                      </div>
                      <div className="w-12 h-12 bg-teal-200 rounded-full flex items-center justify-center">
                        <ArrowDownRight className="w-6 h-6 text-teal-700" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-lg bg-gradient-to-br from-emerald-50 to-emerald-100">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-emerald-600">Total Billed</p>
                        <p className="text-3xl font-bold text-emerald-800">{summary.total_shipments?.toLocaleString()}</p>
                      </div>
                      <div className="w-12 h-12 bg-emerald-200 rounded-full flex items-center justify-center">
                        <TrendingUp className="w-6 h-6 text-emerald-700" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-lg bg-gradient-to-br from-purple-50 to-purple-100">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-purple-600">Days Tracked</p>
                        <p className="text-3xl font-bold text-purple-800">{summary.days_count}</p>
                        {summary.missing_days > 0 && (
                          <p className="text-xs text-amber-600 mt-1">{summary.missing_days} missing</p>
                        )}
                      </div>
                      <div className="w-12 h-12 bg-purple-200 rounded-full flex items-center justify-center">
                        <Calendar className="w-6 h-6 text-purple-700" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-lg bg-gradient-to-br from-indigo-50 to-indigo-100">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-indigo-600">Total Invoice Generated</p>
                        <p className="text-3xl font-bold text-indigo-800" data-testid="total-invoice-count">{totalInvoiceCount}</p>
                      </div>
                      <div className="w-12 h-12 bg-indigo-200 rounded-full flex items-center justify-center">
                        <FileText className="w-6 h-6 text-indigo-700" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-lg bg-gradient-to-br from-teal-50 to-teal-100">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-teal-600">Total Billed Shipments</p>
                        <p className="text-3xl font-bold text-teal-800" data-testid="total-billed-shipments">{totalBilledShipments.toLocaleString()}</p>
                      </div>
                      <div className="w-12 h-12 bg-teal-200 rounded-full flex items-center justify-center">
                        <TrendingUp className="w-6 h-6 text-teal-700" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Data Sub-Tabs: Date/Week/Month Wise */}
            <Tabs value={activeDataTab} onValueChange={(val) => { setActiveDataTab(val); setPage(1); setWeekPage(1); setMonthPage(1); setRiderDailyPage(1); }}>
              <TabsList className="mb-6">
                <TabsTrigger value="date">Date Wise</TabsTrigger>
                <TabsTrigger value="week">Week Wise</TabsTrigger>
                <TabsTrigger value="month">Month Wise</TabsTrigger>
                <TabsTrigger value="rider-daily" data-testid="rider-daily-tab">Date Wise Rider Delivery</TabsTrigger>
              </TabsList>

              {/* Date Wise */}
              <TabsContent value="date">
                <Card className="border-0 shadow-lg">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-lg">Date Wise Billing</CardTitle>
                    <Button variant="outline" size="sm" onClick={() => downloadCSV("date")} disabled={downloading} data-testid="download-date-csv-btn">
                      <Download className="w-4 h-4 mr-2" />
                      {downloading ? "Downloading..." : "Download CSV"}
                    </Button>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-slate-50">
                            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Date</th>
                            <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Forward Delivered</th>
                            <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Return Dispatched</th>
                            <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Total</th>
                            <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Status</th>
                            {settings.billing_frequency === "daily" && (
                              <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Invoice</th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {billingData.map((item, idx) => (
                            <tr key={idx} className="border-b hover:bg-slate-50">
                              <td className="px-4 py-3 align-middle font-medium">{item.date}</td>
                              <td className="px-4 py-3 align-middle text-right text-blue-700">{item.forward_delivered}</td>
                              <td className="px-4 py-3 align-middle text-right text-teal-700">{item.return_dispatched}</td>
                              <td className="px-4 py-3 align-middle text-right font-medium">{item.total}</td>
                              <td className="px-4 py-3 align-middle text-center">
                                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                                  <CheckCircle className="w-3 h-3 mr-1" /> Calculated
                                </Badge>
                              </td>
                              {settings.billing_frequency === "daily" && (
                                <td className="px-4 py-3 align-middle text-center">
                                  {item.invoice_number ? (
                                    <div className="flex flex-col items-center gap-1">
                                      <span className="text-xs font-medium text-green-700">{item.invoice_number}</span>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => downloadExistingInvoice("daily", { date: item.date, existingInvoice: item.invoice_number })}
                                        disabled={generatingInvoice === `existing-daily-${JSON.stringify({ date: item.date, existingInvoice: item.invoice_number })}`}
                                        className="text-green-600 hover:bg-green-50 h-6 px-2"
                                        data-testid={`download-existing-invoice-daily-${item.date}`}
                                      >
                                        {generatingInvoice === `existing-daily-${JSON.stringify({ date: item.date, existingInvoice: item.invoice_number })}` ? (
                                          <Loader2 className="w-3 h-3 animate-spin" />
                                        ) : (
                                          <Download className="w-3 h-3" />
                                        )}
                                      </Button>
                                    </div>
                                  ) : isPeriodComplete(item.date) ? (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => openInvoiceModal("daily", { date: item.date })}
                                      disabled={generatingInvoice === `daily-${JSON.stringify({ date: item.date })}`}
                                      className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                                      data-testid={`generate-invoice-daily-${item.date}`}
                                    >
                                      {generatingInvoice === `daily-${JSON.stringify({ date: item.date })}` ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                      ) : (
                                        <FileText className="w-4 h-4" />
                                      )}
                                    </Button>
                                  ) : (
                                    <span className="text-xs text-slate-400">In Progress</span>
                                  )}
                                </td>
                              )}
                            </tr>
                          ))}
                          {billingData.length === 0 && (
                            <tr>
                              <td colSpan={settings.billing_frequency === "daily" ? 6 : 5} className="p-8 text-center text-slate-400">No billing data available</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="flex items-center justify-between mt-4 pt-4 border-t">
                        <p className="text-sm text-slate-500">Page {page} of {totalPages}</p>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                            <ChevronLeft className="w-4 h-4" />
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Week Wise */}
              <TabsContent value="week">
                <Card className="border-0 shadow-lg">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                      <CardTitle className="text-lg">Week Wise Billing</CardTitle>
                      {weekDefinitions && (
                        <p className="text-xs text-slate-500 mt-1">
                          W1: Days {weekDefinitions.week_1?.start}-{weekDefinitions.week_1?.end} | 
                          W2: Days {weekDefinitions.week_2?.start}-{weekDefinitions.week_2?.end} | 
                          W3: Days {weekDefinitions.week_3?.start}-{weekDefinitions.week_3?.end} | 
                          W4: Days {weekDefinitions.week_4?.start}-end
                        </p>
                      )}
                    </div>
                    <Button variant="outline" size="sm" onClick={() => downloadCSV("week")} disabled={downloading} data-testid="download-week-csv-btn">
                      <Download className="w-4 h-4 mr-2" />
                      {downloading ? "Downloading..." : "Download CSV"}
                    </Button>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-slate-50">
                            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Week</th>
                            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Date Range</th>
                            <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Forward Delivered</th>
                            <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Return Dispatched</th>
                            <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Total</th>
                            <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Days</th>
                            {settings.billing_frequency === "weekly" && (
                              <>
                                <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Invoice</th>
                                <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Invoice Raised</th>
                                <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Amount Received</th>
                              </>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {weeklyData.map((item, idx) => {
                            const monthNames = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                            return (
                              <tr key={idx} className="border-b hover:bg-slate-50">
                                <td className="px-4 py-3 align-middle font-medium">{item.week_label}</td>
                                <td className="px-4 py-3 align-middle text-slate-600">
                                  <div className="whitespace-nowrap">
                                    <div className="text-sm">{item.start_date} → {item.end_date}</div>
                                    {item.week_range && <div className="text-xs text-slate-500">({item.week_range})</div>}
                                  </div>
                                </td>
                                <td className="px-4 py-3 align-middle text-right text-blue-700">{item.forward_delivered}</td>
                                <td className="px-4 py-3 align-middle text-right text-teal-700">{item.return_dispatched}</td>
                                <td className="px-4 py-3 align-middle text-right font-medium">{item.total}</td>
                                <td className="px-4 py-3 align-middle text-center"><Badge variant="secondary">{item.days_count}</Badge></td>
                                {settings.billing_frequency === "weekly" && (
                                  <>
                                  <td className="px-4 py-3 align-middle text-center">
                                    {item.invoice_number ? (
                                      <div className="flex flex-col items-center gap-1">
                                        <span className="text-xs font-medium text-green-700">{item.invoice_number}</span>
                                        {(item.billed_quantity_forward != null || item.billed_quantity_return != null) && (
                                          <span className="text-xs text-slate-500">
                                            Billed: {item.billed_quantity_forward ?? '-'}F / {item.billed_quantity_return ?? '-'}R
                                          </span>
                                        )}
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => downloadExistingInvoice("weekly", { 
                                            year: item.year, 
                                            month: item.month, 
                                            week: item.week,
                                            monthName: monthNames[item.month],
                                            existingInvoice: item.invoice_number
                                          })}
                                          disabled={generatingInvoice?.startsWith(`existing-weekly-`)}
                                          className="text-green-600 hover:bg-green-50 h-6 px-2"
                                          data-testid={`download-existing-invoice-weekly-${item.year}-${item.month}-${item.week}`}
                                        >
                                          {generatingInvoice?.includes(`"year":${item.year},"month":${item.month},"week":${item.week}`) ? (
                                            <Loader2 className="w-3 h-3 animate-spin" />
                                          ) : (
                                            <Download className="w-3 h-3" />
                                          )}
                                        </Button>
                                      </div>
                                    ) : isPeriodComplete(item.end_date) && item.total > 0 ? (
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => openInvoiceModal("weekly", { 
                                          year: item.year, 
                                          month: item.month, 
                                          week: item.week,
                                          monthName: monthNames[item.month],
                                          forward_delivered: item.forward_delivered || 0,
                                          return_dispatched: item.return_dispatched || 0
                                        })}
                                        disabled={generatingInvoice === `weekly-${JSON.stringify({ year: item.year, month: item.month, week: item.week })}`}
                                        className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                                        data-testid={`generate-invoice-weekly-${item.year}-${item.month}-${item.week}`}
                                      >
                                        {generatingInvoice === `weekly-${JSON.stringify({ year: item.year, month: item.month, week: item.week })}` ? (
                                          <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                          <FileText className="w-4 h-4" />
                                        )}
                                      </Button>
                                    ) : (
                                      <span className="text-xs text-slate-400">In Progress</span>
                                    )}
                                  </td>
                                  <td className="px-4 py-3 align-middle text-right">
                                    {item.invoice_amount ? (
                                      <span className="font-medium text-blue-700">
                                        <IndianRupee className="w-3 h-3 inline mr-0.5" />
                                        {Number(item.invoice_amount).toLocaleString("en-IN")}
                                      </span>
                                    ) : (
                                      <span className="text-slate-300">-</span>
                                    )}
                                  </td>
                                  <td className="px-4 py-3 align-middle text-right">
                                    {item.invoice_id ? (
                                      <div className="flex items-center justify-end gap-1.5">
                                        <span className={`font-medium ${item.amount_received >= item.invoice_amount ? "text-emerald-600" : "text-amber-600"}`}>
                                          <IndianRupee className="w-3 h-3 inline mr-0.5" />
                                          {Number(item.amount_received || 0).toLocaleString("en-IN")}
                                        </span>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-6 w-6 p-0 text-emerald-600 hover:bg-emerald-50"
                                          onClick={() => openPaymentDialog(item)}
                                          title="Record Payment"
                                          data-testid={`record-payment-${item.year}-${item.month}-${item.week}`}
                                        >
                                          <CreditCard className="w-3.5 h-3.5" />
                                        </Button>
                                      </div>
                                    ) : (
                                      <span className="text-slate-300">-</span>
                                    )}
                                  </td>
                                </>
                                )}
                              </tr>
                            );
                          })}
                          {weeklyData.length === 0 && (
                            <tr>
                              <td colSpan={settings.billing_frequency === "weekly" ? 9 : 6} className="p-8 text-center text-slate-400">No weekly data available</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                    {weekTotalPages > 1 && (
                      <div className="flex items-center justify-between mt-4 pt-4 border-t">
                        <p className="text-sm text-slate-500">Page {weekPage} of {weekTotalPages}</p>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={() => setWeekPage(p => Math.max(1, p - 1))} disabled={weekPage === 1}>
                            <ChevronLeft className="w-4 h-4" />
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => setWeekPage(p => Math.min(weekTotalPages, p + 1))} disabled={weekPage === weekTotalPages}>
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Month Wise */}
              <TabsContent value="month">
                <Card className="border-0 shadow-lg">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-lg">Month Wise Billing</CardTitle>
                    <Button variant="outline" size="sm" onClick={() => downloadCSV("month")} disabled={downloading} data-testid="download-month-csv-btn">
                      <Download className="w-4 h-4 mr-2" />
                      {downloading ? "Downloading..." : "Download CSV"}
                    </Button>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-slate-50">
                            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Month</th>
                            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Date Range</th>
                            <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Forward Delivered</th>
                            <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Return Dispatched</th>
                            <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Total</th>
                            <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Days</th>
                            {settings.billing_frequency === "monthly" && (
                              <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Invoice</th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {monthlyData.map((item, idx) => {
                            const monthNames = ["", "January", "February", "March", "April", "May", "June", 
                                               "July", "August", "September", "October", "November", "December"];
                            return (
                              <tr key={idx} className="border-b hover:bg-slate-50">
                                <td className="px-4 py-3 align-middle font-medium">{item.month_label}</td>
                                <td className="px-4 py-3 align-middle text-slate-600 text-sm whitespace-nowrap">{item.start_date} → {item.end_date}</td>
                                <td className="px-4 py-3 align-middle text-right text-blue-700">{item.forward_delivered}</td>
                                <td className="px-4 py-3 align-middle text-right text-teal-700">{item.return_dispatched}</td>
                                <td className="px-4 py-3 align-middle text-right font-medium">{item.total}</td>
                                <td className="px-4 py-3 align-middle text-center"><Badge variant="secondary">{item.days_count}</Badge></td>
                                {settings.billing_frequency === "monthly" && (
                                  <td className="px-4 py-3 align-middle text-center">
                                    {item.invoice_number ? (
                                      <div className="flex flex-col items-center gap-1">
                                        <span className="text-xs font-medium text-green-700">{item.invoice_number}</span>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => downloadExistingInvoice("monthly", { 
                                            year: item.year, 
                                            month: item.month,
                                            monthName: monthNames[item.month],
                                            existingInvoice: item.invoice_number
                                          })}
                                          disabled={generatingInvoice?.startsWith(`existing-monthly-`)}
                                          className="text-green-600 hover:bg-green-50 h-6 px-2"
                                          data-testid={`download-existing-invoice-monthly-${item.year}-${item.month}`}
                                        >
                                          {generatingInvoice?.includes(`"year":${item.year},"month":${item.month}`) ? (
                                            <Loader2 className="w-3 h-3 animate-spin" />
                                          ) : (
                                            <Download className="w-3 h-3" />
                                          )}
                                        </Button>
                                      </div>
                                    ) : isPeriodComplete(item.end_date) && item.total > 0 ? (
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => openInvoiceModal("monthly", { 
                                          year: item.year, 
                                          month: item.month,
                                          monthName: monthNames[item.month]
                                        })}
                                        disabled={generatingInvoice === `monthly-${JSON.stringify({ year: item.year, month: item.month })}`}
                                        className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                                        data-testid={`generate-invoice-monthly-${item.year}-${item.month}`}
                                      >
                                        {generatingInvoice === `monthly-${JSON.stringify({ year: item.year, month: item.month })}` ? (
                                          <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                          <FileText className="w-4 h-4" />
                                        )}
                                      </Button>
                                    ) : (
                                      <span className="text-xs text-slate-400">In Progress</span>
                                    )}
                                  </td>
                                )}
                              </tr>
                            );
                          })}
                          {monthlyData.length === 0 && (
                            <tr>
                              <td colSpan={settings.billing_frequency === "monthly" ? 7 : 6} className="p-8 text-center text-slate-400">No monthly data available</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                    {monthTotalPages > 1 && (
                      <div className="flex items-center justify-between mt-4 pt-4 border-t">
                        <p className="text-sm text-slate-500">Page {monthPage} of {monthTotalPages}</p>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={() => setMonthPage(p => Math.max(1, p - 1))} disabled={monthPage === 1}>
                            <ChevronLeft className="w-4 h-4" />
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => setMonthPage(p => Math.min(monthTotalPages, p + 1))} disabled={monthPage === monthTotalPages}>
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Date Wise Rider Delivery */}
              <TabsContent value="rider-daily">
                <Card className="border-0 shadow-lg">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-lg">Date Wise Rider Delivery</CardTitle>
                    <Button variant="outline" size="sm" onClick={() => downloadCSV("rider-daily")} disabled={downloading} data-testid="download-rider-daily-csv-btn">
                      <Download className="w-4 h-4 mr-2" /> {downloading ? "Downloading..." : "Download CSV"}
                    </Button>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="rider-daily-table">
                        <thead>
                          <tr className="border-b bg-muted/50">
                            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Date</th>
                            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Rider Name</th>
                            <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Total Delivered</th>
                            <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Forward Delivered</th>
                            <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Return Picked Up</th>
                          </tr>
                        </thead>
                        <tbody>
                          {riderDailyData.length === 0 ? (
                            <tr><td colSpan={5} className="text-center p-8 text-muted-foreground">No data available</td></tr>
                          ) : riderDailyData.map((row, i) => (
                            <tr key={i} className="border-b hover:bg-muted/30">
                              <td className="px-4 py-3 align-middle">{row.date}</td>
                              <td className="px-4 py-3 align-middle">{row.rider_name}</td>
                              <td className="px-4 py-3 align-middle text-right font-medium">{row.total_delivered}</td>
                              <td className="px-4 py-3 align-middle text-right">{row.forward_delivered}</td>
                              <td className="px-4 py-3 align-middle text-right">{row.return_picked}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {riderDailyTotalPages > 1 && (
                      <div className="flex items-center justify-between mt-4 pt-4 border-t">
                        <span className="text-sm text-muted-foreground">Page {riderDailyPage} of {riderDailyTotalPages}</span>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={() => setRiderDailyPage(p => Math.max(1, p - 1))} disabled={riderDailyPage === 1}>
                            <ChevronLeft className="w-4 h-4" />
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => setRiderDailyPage(p => Math.min(riderDailyTotalPages, p + 1))} disabled={riderDailyPage === riderDailyTotalPages}>
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </TabsContent>

          {/* ============ BILLING SETTINGS TAB ============ */}
          <TabsContent value="settings">
            {settingsLoading ? (
              <div className="flex flex-col items-center justify-center h-[40vh]">
                <Loader2 className="w-12 h-12 animate-spin text-emerald-500" />
                <p className="text-slate-500 mt-4 text-lg">Loading Settings...</p>
              </div>
            ) : (
              <div className="space-y-6">
                {settings.last_updated_at && (
                  <div className="text-sm text-slate-500 bg-slate-50 px-4 py-2 rounded-lg inline-block">
                    Last updated: {formatDate(settings.last_updated_at)} by {settings.last_updated_by || "—"}
                  </div>
                )}

                <Tabs defaultValue="frequency" className="space-y-6">
                  <TabsList className="bg-slate-100">
                    <TabsTrigger value="frequency" className="gap-2"><CalendarDays className="w-4 h-4" /> Frequency</TabsTrigger>
                    <TabsTrigger value="weeks" className="gap-2"><Calendar className="w-4 h-4" /> Week Definitions</TabsTrigger>
                    <TabsTrigger value="charges" className="gap-2"><IndianRupee className="w-4 h-4" /> Charges</TabsTrigger>
                    <TabsTrigger value="parties" className="gap-2"><Building className="w-4 h-4" /> Bill To / Bill From</TabsTrigger>
                  </TabsList>

                  {/* Frequency Tab */}
                  <TabsContent value="frequency">
                    <Card className="border-0 shadow-lg">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-lg">
                          <CalendarDays className="w-5 h-5 text-emerald-600" />
                          Billing Frequency
                        </CardTitle>
                        <CardDescription>Set how often invoices should be generated. This controls which billing view is primary for invoice generation.</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <RadioGroup value={settings.billing_frequency} onValueChange={(val) => setSettings(prev => ({ ...prev, billing_frequency: val }))} className="space-y-4">
                          {[
                            { val: "daily", label: "Daily", desc: "Generate invoices for each day's billing data" },
                            { val: "weekly", label: "Weekly", desc: "Generate invoices aggregated by week" },
                            { val: "monthly", label: "Monthly", desc: "Generate invoices aggregated by month" },
                          ].map(opt => (
                            <div key={opt.val} className="flex items-start space-x-3 p-4 border rounded-lg hover:bg-slate-50 cursor-pointer">
                              <RadioGroupItem value={opt.val} id={`freq-${opt.val}`} className="mt-1" />
                              <div>
                                <Label htmlFor={`freq-${opt.val}`} className="font-medium cursor-pointer">{opt.label}</Label>
                                <p className="text-sm text-slate-500">{opt.desc}</p>
                              </div>
                            </div>
                          ))}
                        </RadioGroup>
                        
                        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                          <p><strong>Note:</strong> Billing cron job settings (enable/disable, run time) are configured on the <a href="/pharmeasy/cron-jobs" className="underline font-medium">Cron Jobs page</a>.</p>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  {/* Week Definitions Tab */}
                  <TabsContent value="weeks">
                    <Card className="border-0 shadow-lg">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-lg">
                          <Calendar className="w-5 h-5 text-indigo-600" />
                          Week Definitions
                        </CardTitle>
                        <CardDescription>
                          Define custom week ranges based on day-of-month. These ranges are used for "Week Wise" billing data aggregation.
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {["week_1", "week_2", "week_3", "week_4"].map((weekKey, idx) => (
                            <div key={weekKey} className="p-4 border rounded-lg bg-slate-50">
                              <p className="font-semibold text-slate-700 mb-3">Week {idx + 1}</p>
                              <div className="flex items-center gap-4">
                                <div className="flex-1">
                                  <Label htmlFor={`${weekKey}-start`} className="text-xs">Start Day</Label>
                                  <Input
                                    id={`${weekKey}-start`}
                                    type="number"
                                    min="1"
                                    max="31"
                                    value={settings.week_definitions?.[weekKey]?.start || 1}
                                    onChange={(e) => updateWeekDef(weekKey, "start", e.target.value)}
                                    className="mt-1"
                                    data-testid={`${weekKey}-start-input`}
                                  />
                                </div>
                                <span className="text-slate-400 mt-5">to</span>
                                <div className="flex-1">
                                  <Label htmlFor={`${weekKey}-end`} className="text-xs">End Day</Label>
                                  <Input
                                    id={`${weekKey}-end`}
                                    type="number"
                                    min="1"
                                    max="31"
                                    value={settings.week_definitions?.[weekKey]?.end || 7}
                                    onChange={(e) => updateWeekDef(weekKey, "end", e.target.value)}
                                    className="mt-1"
                                    data-testid={`${weekKey}-end-input`}
                                  />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                          <p><strong>Note:</strong> Week 4's end day will automatically cap at the actual last day of each month (28/29/30/31).</p>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  {/* Charges Tab */}
                  <TabsContent value="charges">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Forward Order Charges */}
                      <Card className="border-0 shadow-lg">
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2 text-lg">
                            <CreditCard className="w-5 h-5 text-blue-600" />
                            Forward Order Charges
                          </CardTitle>
                          <CardDescription>Configure billing rate for forward (delivery) orders</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                          <div>
                            <Label className="text-sm font-medium">Charge Type</Label>
                            <Select value={settings.forward_charge?.charge_type || "per_order"} onValueChange={(val) => updateForwardCharge("charge_type", val)}>
                              <SelectTrigger className="mt-1" data-testid="forward-charge-type-select"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="per_order">Charge Per Order</SelectItem>
                                <SelectItem value="minimum_billing">Minimum Billing Amount</SelectItem>
                                <SelectItem value="fixed_rate">Fixed Rate (Frequency-wise)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          {settings.forward_charge?.charge_type === "per_order" && (
                            <div>
                              <Label htmlFor="forward-per-order">Charge Per Forward Order (Rs.)</Label>
                              <div className="relative mt-1">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">₹</span>
                                <Input id="forward-per-order" type="number" step="0.01" min="0" value={settings.forward_charge?.charge_per_order || 0} onChange={(e) => updateForwardCharge("charge_per_order", parseFloat(e.target.value) || 0)} className="pl-8" data-testid="forward-per-order-input" />
                              </div>
                            </div>
                          )}
                          {settings.forward_charge?.charge_type === "minimum_billing" && (
                            <div>
                              <Label>Minimum Billing Amount (Rs.)</Label>
                              <div className="relative mt-1">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">₹</span>
                                <Input type="number" step="0.01" min="0" value={settings.forward_charge?.minimum_billing_amount || 0} onChange={(e) => updateForwardCharge("minimum_billing_amount", parseFloat(e.target.value) || 0)} className="pl-8" />
                              </div>
                            </div>
                          )}
                          {settings.forward_charge?.charge_type === "fixed_rate" && (
                            <div className="space-y-4">
                              {["daily", "weekly", "monthly"].map(period => (
                                <div key={period}>
                                  <Label>{period.charAt(0).toUpperCase() + period.slice(1)} Fixed Rate (Rs.)</Label>
                                  <div className="relative mt-1">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">₹</span>
                                    <Input type="number" step="0.01" min="0" value={settings.forward_charge[`fixed_rate_${period}`]} onChange={(e) => updateForwardCharge(`fixed_rate_${period}`, parseFloat(e.target.value) || 0)} className="pl-8" />
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {/* GST Percentage */}
                          <div className="pt-4 border-t">
                            <Label htmlFor="forward-gst">GST % (for invoicing)</Label>
                            <div className="relative mt-1">
                              <Input 
                                id="forward-gst" 
                                type="number" 
                                step="0.5" 
                                min="0" 
                                max="100"
                                value={settings.forward_charge?.gst_percentage || 18} 
                                onChange={(e) => updateForwardCharge("gst_percentage", parseFloat(e.target.value) || 0)} 
                                className="w-24"
                                data-testid="forward-gst-input" 
                              />
                              <p className="text-xs text-slate-400 mt-1">CGST & SGST will be {((settings.forward_charge?.gst_percentage || 18) / 2).toFixed(1)}% each</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Return Order Charges */}
                      <Card className="border-0 shadow-lg">
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2 text-lg">
                            <CreditCard className="w-5 h-5 text-teal-600" />
                            Return Order Charges
                          </CardTitle>
                          <CardDescription>Configure billing rate for return (RTO/RTH) orders</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                          <div>
                            <Label className="text-sm font-medium">Charge Type</Label>
                            <Select value={settings.return_charge?.charge_type || "per_order"} onValueChange={(val) => updateReturnCharge("charge_type", val)}>
                              <SelectTrigger className="mt-1" data-testid="return-charge-type-select"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="per_order">Charge Per Order</SelectItem>
                                <SelectItem value="minimum_billing">Minimum Billing Amount</SelectItem>
                                <SelectItem value="fixed_rate">Fixed Rate (Frequency-wise)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          {settings.return_charge?.charge_type === "per_order" && (
                            <div>
                              <Label htmlFor="return-per-order">Charge Per Return Order (Rs.)</Label>
                              <div className="relative mt-1">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">₹</span>
                                <Input id="return-per-order" type="number" step="0.01" min="0" value={settings.return_charge?.charge_per_order || 0} onChange={(e) => updateReturnCharge("charge_per_order", parseFloat(e.target.value) || 0)} className="pl-8" data-testid="return-per-order-input" />
                              </div>
                            </div>
                          )}
                          {settings.return_charge?.charge_type === "minimum_billing" && (
                            <div>
                              <Label>Minimum Billing Amount (Rs.)</Label>
                              <div className="relative mt-1">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">₹</span>
                                <Input type="number" step="0.01" min="0" value={settings.return_charge?.minimum_billing_amount || 0} onChange={(e) => updateReturnCharge("minimum_billing_amount", parseFloat(e.target.value) || 0)} className="pl-8" />
                              </div>
                            </div>
                          )}
                          {settings.return_charge?.charge_type === "fixed_rate" && (
                            <div className="space-y-4">
                              {["daily", "weekly", "monthly"].map(period => (
                                <div key={period}>
                                  <Label>{period.charAt(0).toUpperCase() + period.slice(1)} Fixed Rate (Rs.)</Label>
                                  <div className="relative mt-1">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">₹</span>
                                    <Input type="number" step="0.01" min="0" value={settings.return_charge[`fixed_rate_${period}`]} onChange={(e) => updateReturnCharge(`fixed_rate_${period}`, parseFloat(e.target.value) || 0)} className="pl-8" />
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {/* GST Percentage */}
                          <div className="pt-4 border-t">
                            <Label htmlFor="return-gst">GST % (for invoicing)</Label>
                            <div className="relative mt-1">
                              <Input 
                                id="return-gst" 
                                type="number" 
                                step="0.5" 
                                min="0" 
                                max="100"
                                value={settings.return_charge?.gst_percentage || 18} 
                                onChange={(e) => updateReturnCharge("gst_percentage", parseFloat(e.target.value) || 0)} 
                                className="w-24"
                                data-testid="return-gst-input" 
                              />
                              <p className="text-xs text-slate-400 mt-1">CGST & SGST will be {((settings.return_charge?.gst_percentage || 18) / 2).toFixed(1)}% each</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </TabsContent>

                  {/* Bill To / Bill From Tab */}
                  <TabsContent value="parties">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <Card className="border-0 shadow-lg">
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2 text-lg">
                            <Building className="w-5 h-5 text-blue-600" />
                            Bill To (Customer)
                          </CardTitle>
                          <CardDescription>Pharmeasy billing information for invoices</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div>
                            <Label htmlFor="bill-to-name">Company Name *</Label>
                            <Input id="bill-to-name" value={settings.bill_to?.name || ""} onChange={(e) => updateBillTo("name", e.target.value)} placeholder="Enter company name" className="mt-1" data-testid="bill-to-name-input" />
                          </div>
                          <div>
                            <Label htmlFor="bill-to-address">Address *</Label>
                            <Textarea id="bill-to-address" value={settings.bill_to?.address || ""} onChange={(e) => updateBillTo("address", e.target.value)} placeholder="Enter full address" className="mt-1" rows={3} data-testid="bill-to-address-input" />
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label htmlFor="bill-to-gst">GST Number *</Label>
                              <Input id="bill-to-gst" value={settings.bill_to?.gst_number || ""} onChange={(e) => updateBillTo("gst_number", e.target.value.toUpperCase())} placeholder="e.g., 27AABCU9603R1ZM" className="mt-1 uppercase" maxLength={15} data-testid="bill-to-gst-input" />
                            </div>
                            <div>
                              <Label htmlFor="bill-to-state">State Code *</Label>
                              <Input id="bill-to-state" value={settings.bill_to?.state_code || ""} onChange={(e) => updateBillTo("state_code", e.target.value)} placeholder="e.g., 27" className="mt-1" maxLength={2} data-testid="bill-to-state-input" />
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      <Card className="border-0 shadow-lg">
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2 text-lg">
                            <Building className="w-5 h-5 text-emerald-600" />
                            Bill From (Service Provider)
                          </CardTitle>
                          <CardDescription>Your company billing information for invoices</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div>
                            <Label htmlFor="bill-from-name">Company Name *</Label>
                            <Input id="bill-from-name" value={settings.bill_from?.name || ""} onChange={(e) => updateBillFrom("name", e.target.value)} placeholder="Enter company name" className="mt-1" data-testid="bill-from-name-input" />
                          </div>
                          <div>
                            <Label htmlFor="bill-from-address">Address *</Label>
                            <Textarea id="bill-from-address" value={settings.bill_from?.address || ""} onChange={(e) => updateBillFrom("address", e.target.value)} placeholder="Enter full address" className="mt-1" rows={3} data-testid="bill-from-address-input" />
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label htmlFor="bill-from-gst">GST Number *</Label>
                              <Input id="bill-from-gst" value={settings.bill_from?.gst_number || ""} onChange={(e) => updateBillFrom("gst_number", e.target.value.toUpperCase())} placeholder="e.g., 27AABCU9603R1ZM" className="mt-1 uppercase" maxLength={15} data-testid="bill-from-gst-input" />
                            </div>
                            <div>
                              <Label htmlFor="bill-from-state">State Code *</Label>
                              <Input id="bill-from-state" value={settings.bill_from?.state_code || ""} onChange={(e) => updateBillFrom("state_code", e.target.value)} placeholder="e.g., 27" className="mt-1" maxLength={2} data-testid="bill-from-state-input" />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Bank Details */}
                    <Card className="border-0 shadow-lg mt-6" data-testid="bank-details-card">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-lg">
                          <Landmark className="w-5 h-5 text-teal-600" />
                          Bank Details (for Invoice)
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="bank-account">Bank Account Number</Label>
                            <Input id="bank-account" value={settings.bank_details?.account_number || ""} onChange={(e) => updateBankDetails("account_number", e.target.value)} placeholder="e.g., 195605000175" className="mt-1" data-testid="bank-account-input" />
                          </div>
                          <div>
                            <Label htmlFor="bank-name">Bank Name</Label>
                            <Input id="bank-name" value={settings.bank_details?.bank_name || ""} onChange={(e) => updateBankDetails("bank_name", e.target.value.toUpperCase())} placeholder="e.g., ICICI BANK LIMITED" className="mt-1 uppercase" data-testid="bank-name-input" />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="bank-ifsc">IFSC Code</Label>
                            <Input id="bank-ifsc" value={settings.bank_details?.ifsc_code || ""} onChange={(e) => updateBankDetails("ifsc_code", e.target.value.toUpperCase())} placeholder="e.g., ICIC0001956" className="mt-1 uppercase" maxLength={11} data-testid="bank-ifsc-input" />
                          </div>
                          <div>
                            <Label htmlFor="bank-city">City</Label>
                            <Input id="bank-city" value={settings.bank_details?.city || ""} onChange={(e) => updateBankDetails("city", e.target.value.toUpperCase())} placeholder="e.g., MUMBAI" className="mt-1 uppercase" data-testid="bank-city-input" />
                          </div>
                        </div>
                        <div>
                          <Label htmlFor="cheque-payable">For Cheque, Payable To</Label>
                          <Input id="cheque-payable" value={settings.bank_details?.cheque_payable_to || ""} onChange={(e) => updateBankDetails("cheque_payable_to", e.target.value.toUpperCase())} placeholder="e.g., OPTISUPPLY CHAIN SOLUTION PRIVATE LIMITED" className="mt-1 uppercase" data-testid="cheque-payable-input" />
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="border-0 shadow-lg mt-6">
                      <CardContent className="pt-6">
                        <p className="text-sm text-slate-600 mb-2 font-medium">Common GST State Codes:</p>
                        <div className="flex flex-wrap gap-2 text-xs">
                          {[
                            { code: "27", state: "Maharashtra" }, { code: "29", state: "Karnataka" },
                            { code: "36", state: "Telangana" }, { code: "33", state: "Tamil Nadu" },
                            { code: "07", state: "Delhi" }, { code: "06", state: "Haryana" },
                            { code: "09", state: "Uttar Pradesh" }, { code: "24", state: "Gujarat" },
                            { code: "19", state: "West Bengal" }, { code: "32", state: "Kerala" },
                          ].map(item => (
                            <span key={item.code} className="bg-slate-100 px-2 py-1 rounded">{item.code} - {item.state}</span>
                          ))}
                        </div>
                      </CardContent>
                    </Card>

                    {/* Authorized Signatory Image Upload */}
                    <Card className="border-0 shadow-lg mt-6" data-testid="signatory-image-card">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-lg">
                          <FileText className="w-5 h-5 text-teal-600" />
                          Authorized Signatory Image
                        </CardTitle>
                        <p className="text-sm text-slate-500">Upload a signature image to appear on invoices (PNG/JPEG, max 500KB)</p>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-start gap-6">
                          {/* Preview */}
                          <div className="flex-shrink-0">
                            {settings.signatory_image_url ? (
                              <div className="border-2 border-dashed border-teal-200 rounded-lg p-2 bg-teal-50">
                                <img 
                                  src={settings.signatory_image_url} 
                                  alt="Authorized Signatory" 
                                  className="h-16 w-auto object-contain"
                                  data-testid="signatory-image-preview"
                                />
                              </div>
                            ) : (
                              <div className="border-2 border-dashed border-slate-200 rounded-lg p-4 bg-slate-50 text-slate-400 text-sm text-center w-32 h-20 flex items-center justify-center">
                                No image
                              </div>
                            )}
                          </div>
                          
                          {/* Upload/Remove buttons */}
                          <div className="flex flex-col gap-2">
                            <label className="cursor-pointer">
                              <input
                                type="file"
                                accept="image/png,image/jpeg,image/jpg"
                                onChange={handleSignatoryUpload}
                                className="hidden"
                                data-testid="signatory-image-input"
                              />
                              <Button 
                                type="button" 
                                variant="outline" 
                                disabled={uploadingSignatory}
                                className="text-teal-600 border-teal-300 hover:bg-teal-50"
                                asChild
                              >
                                <span>
                                  {uploadingSignatory ? (
                                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>
                                  ) : (
                                    <><Download className="w-4 h-4 mr-2 rotate-180" /> Upload Image</>
                                  )}
                                </span>
                              </Button>
                            </label>
                            
                            {settings.signatory_image_url && (
                              <Button 
                                type="button" 
                                variant="outline" 
                                onClick={handleRemoveSignatory}
                                className="text-red-600 border-red-200 hover:bg-red-50"
                                data-testid="remove-signatory-btn"
                              >
                                <X className="w-4 h-4 mr-2" /> Remove
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>
                </Tabs>

                {/* Save Button at Bottom */}
                <div className="flex justify-end pt-4 border-t">
                  <Button onClick={handleSaveSettings} disabled={saving} style={{ backgroundColor: "#1B8C3D" }} size="lg" data-testid="save-billing-settings-btn-bottom">
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    {saving ? "Saving..." : "Save All Settings"}
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
          </TabsContent>

          {/* ============ VENDOR TAB ============ */}
          <TabsContent value="vendor">
            <VendorExpenses />
          </TabsContent>
        </Tabs>

        {/* Invoice Number Modal */}
        <Dialog open={invoiceModalOpen} onOpenChange={setInvoiceModalOpen}>
          <DialogContent className="sm:max-w-md" data-testid="invoice-number-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-indigo-600" />
                Generate Invoice
              </DialogTitle>
              <DialogDescription>
                Verify billing quantities and enter the invoice number.
                {pendingInvoiceParams && (
                  <span className="block mt-2 text-slate-700 font-medium">
                    {pendingInvoiceParams.type === "daily" && `Date: ${pendingInvoiceParams.params.date}`}
                    {pendingInvoiceParams.type === "weekly" && `Week: ${pendingInvoiceParams.params.week} of ${pendingInvoiceParams.params.monthName} ${pendingInvoiceParams.params.year}`}
                    {pendingInvoiceParams.type === "monthly" && `Month: ${pendingInvoiceParams.params.monthName} ${pendingInvoiceParams.params.year}`}
                  </span>
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-4">
              {/* Billing Quantities */}
              <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                <p className="text-sm font-semibold text-slate-700">Billing Quantities</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="billed-forward" className="text-xs text-slate-600">
                      Forward Delivered
                    </Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input
                        id="billed-forward"
                        type="number"
                        min={0}
                        value={billedForward}
                        onChange={(e) => setBilledForward(parseInt(e.target.value) || 0)}
                        className="h-9"
                        data-testid="billed-forward-input"
                      />
                      {pendingInvoiceParams?.params?.forward_delivered != null && billedForward !== pendingInvoiceParams.params.forward_delivered && (
                        <span className="text-xs text-amber-600 whitespace-nowrap">was {pendingInvoiceParams.params.forward_delivered}</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="billed-return" className="text-xs text-slate-600">
                      Return Picked Up
                    </Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input
                        id="billed-return"
                        type="number"
                        min={0}
                        value={billedReturn}
                        onChange={(e) => setBilledReturn(parseInt(e.target.value) || 0)}
                        className="h-9"
                        data-testid="billed-return-input"
                      />
                      {pendingInvoiceParams?.params?.return_dispatched != null && billedReturn !== pendingInvoiceParams.params.return_dispatched && (
                        <span className="text-xs text-amber-600 whitespace-nowrap">was {pendingInvoiceParams.params.return_dispatched}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="text-right text-sm font-semibold text-slate-800 pt-1 border-t border-slate-200">
                  Total: {billedForward + billedReturn}
                </div>
              </div>

              {/* Invoice Date */}
              <div>
                <Label className="text-sm font-medium">Invoice Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full mt-2 justify-start text-left font-normal" data-testid="pharmeasy-invoice-date-trigger">
                      <Calendar className="mr-2 h-4 w-4" />
                      {format(invoiceDate, "dd/MM/yyyy")}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarPicker mode="single" selected={invoiceDate} onSelect={(d) => d && setInvoiceDate(d)} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Invoice Number */}
              <div>
                <Label htmlFor="invoice-number" className="text-sm font-medium">
                  Invoice Number <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="invoice-number"
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  placeholder="e.g., INV001/25-26 or EDGE-2025-001"
                  className="mt-2"
                  data-testid="invoice-number-input"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && invoiceNumber.trim()) {
                      generateInvoice();
                    }
                  }}
                />
                <p className="text-xs text-slate-500 mt-2">
                  Enter your custom invoice number format (e.g., INV298/25-26)
                </p>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={closeInvoiceModal} data-testid="invoice-cancel-btn">
                Cancel
              </Button>
              <Button 
                onClick={generateInvoice} 
                disabled={!invoiceNumber.trim()}
                style={{ backgroundColor: "#1B8C3D" }}
                data-testid="invoice-generate-btn"
              >
                <FileText className="w-4 h-4 mr-2" />
                Generate PDF
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Payment Recording Dialog */}
        <Dialog open={!!paymentDialog} onOpenChange={(open) => { if (!open) setPaymentDialog(null); }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Record Payment Received</DialogTitle>
              {paymentDialog && (
                <DialogDescription>
                  {paymentDialog.invoice_number || paymentDialog.week_label} — Invoice: <IndianRupee className="w-3 h-3 inline" />{Number(paymentDialog.invoice_amount || 0).toLocaleString("en-IN")} | Received: <IndianRupee className="w-3 h-3 inline" />{Number(paymentDialog.amount_received || 0).toLocaleString("en-IN")} | Due: <IndianRupee className="w-3 h-3 inline" />{Number((paymentDialog.invoice_amount || 0) - (paymentDialog.amount_received || 0)).toLocaleString("en-IN")}
                </DialogDescription>
              )}
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Amount *</Label>
                <Input type="number" value={paymentForm.amount} onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })} placeholder="0" />
              </div>
              <div>
                <Label className="text-xs">Payment Date *</Label>
                <Input type="date" value={paymentForm.payment_date} onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Payment Mode</Label>
                <Input value={paymentForm.payment_mode} onChange={(e) => setPaymentForm({ ...paymentForm, payment_mode: e.target.value })} placeholder="Bank Transfer, UPI, Cheque..." />
              </div>
              <div>
                <Label className="text-xs">Reference Number</Label>
                <Input value={paymentForm.reference_number} onChange={(e) => setPaymentForm({ ...paymentForm, reference_number: e.target.value })} placeholder="Transaction / UTR ref" />
              </div>
              <div>
                <Label className="text-xs">Notes</Label>
                <Input value={paymentForm.notes} onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })} placeholder="Optional notes" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPaymentDialog(null)}>Cancel</Button>
              <Button onClick={handleRecordPayment} disabled={recordingPayment || !paymentForm.amount} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                {recordingPayment ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CreditCard className="w-4 h-4 mr-1" />}
                Record Payment
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </PharmeasyLayout>
  );
};

export default PharmeasyBilling;
